import * as React from 'react'
import * as Sentry from '@sentry/browser'
import { FC } from 'react'
import { FormGroup } from '@blueprintjs/core'
import { GoodwillfindsListing } from '../../../../../types/api/listings'
import Client from '../../../../../client'
import { useGoodwillfindsChannelFormContext } from '../../../../products_v4/listings/goodwillfinds/GoodwillfindsChannelFormContext'
import { LoadableSelect } from '../../../../../components/Inputs'
import { GutterTooltipError } from '../GutterTooltipError'
import { renderError } from '../Helpers'
import { findGoodFuzzyMatch } from '../../../../../utils/string'
import { ProductFormGroup } from '../ProductFormGroup'

interface Props {
  form: GoodwillfindsListing
  onFormChange: ({ name, value }: { name: string; value: object }) => void
  sectionOpen?: boolean
  error: any
  errorTooltipOffset?: number
  errorTooltipPosition?: any
}

export const Suppliers: FC<Props> = ({ error, errorTooltipOffset, errorTooltipPosition }) => {
  const { form, onListingChange, productSupplierId } = useGoodwillfindsChannelFormContext()

  const defaultSupplierProcessor = d => {
    // Auto-choose the closest matching supplier when we initialize this component.
    // Right now, Products will usually have supplier set (because they were manifested).
    // But we haven't directly mapped Supplier to the GWF suppliers.
    // Matching them at runtime like this should work as long as Lister supplier names match the GWF supplier names.
    const autofill = async () => {
      try {
        // When the user chooses a product supplier on this form, we only have ID available.
        // I wish we were using a library for fetching data, because the SupplierSelect is already fetching a list of suppliers.
        // But we aren't, so we need more info to get the supplier name.
        // We don't want to force the form to manage both ID and string name.
        const res = await Client.getSupplier(productSupplierId)
        const productSupplierName = res?.data?.supplier?.name

        const choices: string[] = d.suppliers.map(s => s.name)
        const matchingSupplierName = findGoodFuzzyMatch(productSupplierName, choices)

        if (matchingSupplierName) {
          onListingChange({ name: 'supplierName', value: matchingSupplierName })
        }
      } catch (e) {
        Sentry.captureException(e)
      }
    }
    autofill() // ignore this promise. Let it complete after defaultSupplierProcessor returns.

    return d.suppliers.map(s => ({
      value: s.name,
      label: s.name,
      disabled: false
    }))
  }

  const supplierElement = (
    <LoadableSelect
      load={() => Client.goodwillFindsSuppliers()}
      process={defaultSupplierProcessor}
      onChange={v => onListingChange({ name: 'supplierName', value: v })}
      style={{ marginTop: 0 }}
      value={form.supplierName}
      autoload
    />
  )
  return (
    <>
      <div>
        {errorTooltipOffset && (
          <FormGroup label="Supplier Name" helperText={renderError(error)}>
            <GutterTooltipError isOpen={error} offsetPx={errorTooltipOffset} position={errorTooltipPosition}>
              {supplierElement}
            </GutterTooltipError>
          </FormGroup>
        )}
        {!errorTooltipOffset && (
          <ProductFormGroup title="Supplier Name" error={error} showRequiredLabel helperText={renderError(error)}>
            {supplierElement}
          </ProductFormGroup>
        )}
      </div>
    </>
  )
}
